import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Copy, Loader2, Pencil, Plus, Trash2, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { canonicalUrl } from "@/lib/canonical";
import { useAuth } from "@/context/AuthContext";
import Mandala from "@/components/Mandala";
import RebookModal from "@/components/reiki/RebookModal";
import ModuleProgressTracker from "@/components/lms/ModuleProgressTracker";
import { toast } from "sonner";

// ─────────────────────────  STUDENT DASHBOARD — "My Courses"  ─────────────────────────

export function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [evals, setEvals] = useState({ count: 0, share_url: "" });
  const [hours, setHours] = useState({ total_hours: 0 });
  const [selfEvals, setSelfEvals] = useState({ count: 0 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [reikiBookings, setReikiBookings] = useState([]);
  const [rebookFor, setRebookFor] = useState(null);
  const [moduleProgress, setModuleProgress] = useState([]);

  const loadReiki = async () => {
    try {
      const { data } = await api.get("/reiki/bookings/me");
      setReikiBookings(Array.isArray(data) ? data : []);
    } catch {
      setReikiBookings([]);
    }
  };

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [c, e, h, s] = await Promise.all([
          api.get("/lms/my-courses"),
          api.get("/lms/student-evaluations/me"),
          api.get("/lms/teaching-hours"),
          api.get("/lms/self-evaluation/me"),
        ]);
        setCourses(c.data); setEvals(e.data); setHours(h.data); setSelfEvals(s.data);
      } catch {}
      try { setModuleProgress((await api.get("/lms/my-module-progress")).data); } catch { setModuleProgress([]); }
      await loadReiki();
      setLoading(false);
    })();
  }, [user]);

  if (loading) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;

  const shareLink = canonicalUrl(`/evaluate/${user?.id}`);

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="student-dashboard">
      <div className="container-aviana max-w-5xl">
        <p className="overline-plum mb-5">Your sanctuary</p>
        <h1 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.02]">
          Welcome back, <em className="italic font-serif gold-text">{user?.name?.split(" ")[0] || "sister"}.</em>
        </h1>

        {courses.length === 0 ? (
          <div className="mt-14 bg-aviana-cream rounded-2xl p-10 border border-aviana-border text-center">
            <Mandala size={72} variant="lotus" className="text-aviana-gold mx-auto opacity-70 mb-5" />
            <h2 className="font-display text-2xl text-aviana-ink">No courses yet.</h2>
            <p className="mt-3 text-aviana-muted">Explore the offerings to begin your journey.</p>
            <Link to="/courses/restorative-aerial-yoga-teacher-training" className="btn-gold mt-7 inline-flex">Explore Restorative YTT</Link>
          </div>
        ) : (
          <>
            <h2 className="font-display text-3xl text-aviana-ink mt-14 mb-6">Your courses</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {courses.map(({ course, enrollment }) => (
                <Link key={course.wp_post_id} to={`/courses/${course.slug}/learn`} className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border hover:border-aviana-gold/60 transition-all group" data-testid={`course-card-${course.slug}`}>
                  <p className="overline-plum mb-3">Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}</p>
                  <h3 className="font-display text-2xl text-aviana-ink group-hover:text-aviana-plum transition-colors">{course.title}</h3>
                  <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-aviana-gold">Continue →</p>
                </Link>
              ))}
            </div>
          </>
        )}

        {moduleProgress.length > 0 && (
          <section className="mt-16" data-testid="my-course-progress">
            <h2 className="font-display text-3xl text-aviana-ink mb-6">Your progress</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {moduleProgress.map((p) => (
                <div key={p.course.wp_post_id} className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border" data-testid={`progress-card-${p.course.slug}`}>
                  <p className="overline-plum mb-2">Course progress</p>
                  <h3 className="font-display text-2xl text-aviana-ink mb-5">{p.course.title}</h3>
                  <ModuleProgressTracker progress={p} testid={`progress-${p.course.slug}`} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Module 3 widgets: only shown if enrolled in any in-person YTT modules */}
        {courses.some((c) => c.course.slug === "aerial-yoga-teacher-training-modules") && (
          <>
            <h2 className="font-display text-3xl text-aviana-ink mt-16 mb-6">Module 3 — Practice teaching</h2>
            <div className="grid md:grid-cols-3 gap-5">
              <Link to="/dashboard/student-evaluations" className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border hover:border-aviana-gold/60 group">
                <p className="overline-plum mb-3">Student evaluations</p>
                <p className="font-figure text-4xl text-aviana-ink">{evals.count}<span className="text-aviana-muted text-base">/10</span></p>
                <p className="mt-2 text-[12px] text-aviana-muted">Sent by your practice students</p>
                <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-aviana-gold">Share link →</p>
              </Link>
              <Link to="/dashboard/self-evaluations" className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border hover:border-aviana-gold/60 group">
                <p className="overline-plum mb-3">Self-evaluations</p>
                <p className="font-figure text-4xl text-aviana-ink">{selfEvals.count}<span className="text-aviana-muted text-base">/5</span></p>
                <p className="mt-2 text-[12px] text-aviana-muted">Reflections you've completed</p>
                <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-aviana-gold">Add one →</p>
              </Link>
              <Link to="/dashboard/teaching-hours" className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border hover:border-aviana-gold/60 group">
                <p className="overline-plum mb-3">Teaching hours</p>
                <p className="font-figure text-4xl text-aviana-ink">{hours.total_hours || 0}<span className="text-aviana-muted text-base">/20</span></p>
                <p className="mt-2 text-[12px] text-aviana-muted">Hours logged toward Module 3</p>
                <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-aviana-gold">Log a session →</p>
              </Link>
            </div>

            {/* Share link block */}
            <div className="mt-8 bg-aviana-plumDeep rounded-2xl p-7 text-aviana-cream">
              <p className="overline text-aviana-goldSoft mb-3">Your student-evaluation link</p>
              <p className="font-serif italic text-[15px] mb-4 text-aviana-cream/85">Share this with the students you teach. They'll fill out a short form — it counts toward your 10 required evaluations automatically.</p>
              <div className="flex items-center gap-3 bg-aviana-plumDeep/60 rounded-lg px-4 py-3 border border-aviana-gold/30">
                <code className="text-[13px] text-aviana-goldSoft flex-1 truncate" data-testid="share-link">{shareLink}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="text-aviana-goldSoft hover:text-aviana-cream"
                  data-testid="copy-share-link"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          </>
        )}

        {reikiBookings.length > 0 && (
          <section className="mt-16" data-testid="my-reiki-bookings">
            <h2 className="font-display text-3xl text-aviana-ink mb-6 flex items-center gap-3">
              <Sparkles size={22} className="text-aviana-gold" /> Your reiki sessions
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {reikiBookings.map((b) => (
                <ReikiBookingCard key={b.id} booking={b} onRebook={() => setRebookFor(b)} />
              ))}
            </div>
          </section>
        )}

      </div>

      {rebookFor && (
        <RebookModal
          source={rebookFor}
          endpoint={`/reiki/bookings/${rebookFor.id}/rebook`}
          onClose={() => setRebookFor(null)}
          onSaved={async () => { setRebookFor(null); await loadReiki(); }}
        />
      )}
    </div>
  );
}

function ReikiBookingCard({ booking, onRebook }) {
  const start = new Date(booking.start_iso);
  const statusLabel = booking.status?.replace(/_/g, " ") || "";
  const hasCardOnFile = !!booking.hold_payment_method_id;
  return (
    <div className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border" data-testid={`reiki-booking-${booking.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="overline-plum mb-3">{booking.tier_name}</p>
          <h3 className="font-display text-2xl text-aviana-ink leading-tight">
            {start.toLocaleString([], { weekday: "long", month: "long", day: "numeric" })}
          </h3>
          <p className="mt-1 text-sm text-aviana-muted">
            {start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {booking.duration_min} min · {booking.attendee_count} attendee{booking.attendee_count === 1 ? "" : "s"}
          </p>
          {booking.is_rebook && (
            <p className="mt-1 text-[11px] tracking-[0.18em] uppercase text-aviana-goldDeep">Rebooked follow-up</p>
          )}
        </div>
        <span className={`text-[10px] tracking-[0.18em] uppercase px-3 py-1 rounded-full whitespace-nowrap ${booking.status === "confirmed" ? "bg-aviana-sage/20 text-aviana-sage" : booking.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-aviana-surface text-aviana-ink"}`}>
          {statusLabel}
        </span>
      </div>
      {hasCardOnFile && booking.status !== "cancelled" && (
        <button
          onClick={onRebook}
          className="mt-5 text-[11px] tracking-[0.22em] uppercase text-aviana-gold hover:text-aviana-goldDeep inline-flex items-center gap-1.5"
          data-testid={`reiki-book-another-${booking.id}`}
        >
          <Sparkles size={12} /> Book another session →
        </button>
      )}
    </div>
  );
}

// ─────────────────────────  PUBLIC: STUDENT EVALUATION FORM  ─────────────────────────

export function StudentEvaluationForm() {
  const { instructorId } = useParams();
  const [data, setData] = useState(null);
  const [header, setHeader] = useState({ student_name: "", student_email: "", class_date: new Date().toISOString().slice(0, 10), location: "", type_of_class: "Aerial Yoga" });
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lms/evaluate/${instructorId}`);
        setData(data);
      } catch { toast.error("Could not load evaluation form"); }
    })();
  }, [instructorId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!header.student_name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      await api.post(`/lms/evaluate/${instructorId}`, {
        student_name: header.student_name,
        student_email: header.student_email,
        class_date: header.class_date,
        location: header.location,
        answers: { ...answers, _type_of_class: header.type_of_class },
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;

  if (submitted) return (
    <div className="pt-36 pb-24 min-h-[80vh] flex items-center bg-aviana-surface" data-testid="eval-submitted">
      <div className="container-aviana max-w-2xl text-center">
        <Mandala size={120} variant="rose" className="text-aviana-gold mx-auto mb-6 animate-spin-slow" />
        <p className="overline-plum mb-3">Thank you</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Your evaluation has been received.</h1>
        <p className="mt-7 text-aviana-muted">Your reflections help {data.instructor.name} grow as a teacher. With love &amp; light.</p>
      </div>
    </div>
  );

  return (
    <div className="pt-32 pb-24 bg-aviana-surface min-h-screen" data-testid="evaluation-form-page">
      <form onSubmit={submit} className="container-aviana max-w-3xl">
        <div className="text-center mb-10">
          <Mandala size={64} variant="lotus" className="text-aviana-gold mx-auto mb-5" />
          <p className="overline-plum mb-3">Student evaluation</p>
          <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
            For <em className="italic font-serif gold-text">{data.instructor.name}.</em>
          </h1>
          <p className="mt-6 font-serif italic text-aviana-muted max-w-xl mx-auto">
            Your reflections support an instructor in their Aviana training. Thank you for taking a few minutes to share.
          </p>
        </div>

        {/* Header fields */}
        <div className="bg-aviana-cream rounded-2xl p-8 border border-aviana-border space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Your name" value={header.student_name} onChange={(v) => setHeader({...header, student_name: v})} required testid="student-name" />
            <Field label="Email (optional)" type="email" value={header.student_email} onChange={(v) => setHeader({...header, student_email: v})} testid="student-email" />
            <Field label="Date of class" type="date" value={header.class_date} onChange={(v) => setHeader({...header, class_date: v})} testid="class-date" />
            <Field label="Location" value={header.location} onChange={(v) => setHeader({...header, location: v})} testid="class-location" />
          </div>
          <Field label="Type of class taken" value={header.type_of_class} onChange={(v) => setHeader({...header, type_of_class: v})} testid="class-type" />
        </div>

        {/* Questions */}
        <div className="mt-8 space-y-5">
          {data.questions.map((q, i) => (
            <article key={q.id} className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border">
              <p className="overline text-[10px] mb-3">Question {i + 1}</p>
              <div className="aviana-richtext text-aviana-ink" dangerouslySetInnerHTML={{ __html: q.question_html || q.title || "" }} />
              {q.choices && q.choices.length > 0 ? (
                <div className="mt-4 space-y-2">
                  {q.choices.map((c, cIdx) => (
                    <label key={cIdx} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${answers[q.id] === cIdx ? "border-aviana-gold bg-aviana-gold/5" : "border-aviana-border"}`}>
                      <input type="radio" name={q.id} checked={answers[q.id] === cIdx} onChange={() => setAnswers({...answers, [q.id]: cIdx})} />
                      <span className="text-aviana-ink">{c.text}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <textarea
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers({...answers, [q.id]: e.target.value})}
                  className="mt-4 w-full bg-white border border-aviana-border rounded-lg px-4 py-3 outline-none focus:border-aviana-gold/60"
                  rows={3}
                  placeholder="Your reflection…"
                  data-testid={`eval-answer-${q.id}`}
                />
              )}
            </article>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <button type="submit" disabled={busy} className="btn-gold" data-testid="submit-evaluation">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <>Submit evaluation <ArrowRight size={16} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted">{label}{required && " *"}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-2 w-full bg-white border border-aviana-border rounded-lg px-4 py-2.5 outline-none focus:border-aviana-gold/60"
        data-testid={testid}
      />
    </label>
  );
}

// ─────────────────────────  STUDENT: SELF-EVALUATION  ─────────────────────────

export function SelfEvaluationPage() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [mine, setMine] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [header, setHeader] = useState({ class_date: new Date().toISOString().slice(0, 10), number_of_students: 1, type_of_class: "group", style_focus: "", essential_oils: "" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const [f, m] = await Promise.all([
          api.get("/lms/self-evaluation/form"),
          api.get("/lms/self-evaluation/me"),
        ]);
        setForm(f.data); setMine(m.data.items);
      } catch {}
    })();
  }, []);

  const isAnswered = (q) => {
    const a = answers[q.id];
    if (q.choices && q.choices.length > 0) return a !== undefined && a !== null && a !== "";
    return typeof a === "string" && a.trim().length > 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    // Gate: Module 3 self-eval requires every question answered before save.
    const blanks = new Set(form.questions.filter((q) => !isAnswered(q)).map((q) => q.id));
    if (blanks.size > 0) {
      setMissing(blanks);
      toast.error(`Please answer all ${blanks.size} remaining question${blanks.size === 1 ? "" : "s"}.`);
      const first = document.querySelector(`[data-testid="se-question-${[...blanks][0]}"]`);
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/lms/self-evaluation", { ...header, answers });
      toast.success("Self-evaluation saved");
      setShowForm(false); setAnswers({}); setMissing(new Set());
      const { data } = await api.get("/lms/self-evaluation/me");
      setMine(data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!form) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="self-eval-page">
      <div className="container-aviana max-w-4xl">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> My dashboard
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline-plum mb-3">Module 3</p>
            <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Self-Evaluations <em className="italic gold-text">({mine.length}/5)</em></h1>
          </div>
          <button onClick={() => setShowForm(true)} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="add-self-eval-btn">
            <Plus size={14} /> Add a self-evaluation
          </button>
        </div>

        {showForm ? (
          <form onSubmit={submit} className="bg-aviana-cream rounded-2xl p-8 border border-aviana-border">
            <p className="overline-plum mb-3">New self-evaluation</p>
            <h2 className="font-display text-3xl text-aviana-ink mb-7">{form.quiz.title}</h2>

            <div className="grid md:grid-cols-3 gap-4 mb-7">
              <Field label="Date" type="date" value={header.class_date} onChange={(v) => setHeader({...header, class_date: v})} testid="se-date" />
              <Field label="# of Students" type="number" value={header.number_of_students} onChange={(v) => setHeader({...header, number_of_students: parseInt(v) || 1})} testid="se-students" />
              <label className="block">
                <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted">Type of class</span>
                <select value={header.type_of_class} onChange={(e) => setHeader({...header, type_of_class: e.target.value})} className="mt-2 w-full bg-white border border-aviana-border rounded-lg px-4 py-2.5" data-testid="se-type">
                  <option value="private">Private</option>
                  <option value="semi-private">Semi-Private</option>
                  <option value="group">Group</option>
                </select>
              </label>
              <Field label="Style/Focus of class" value={header.style_focus} onChange={(v) => setHeader({...header, style_focus: v})} testid="se-style" />
              <Field label="Essential oils used" value={header.essential_oils} onChange={(v) => setHeader({...header, essential_oils: v})} testid="se-oils" />
            </div>

            <div className="space-y-5">
              {form.questions.map((q, i) => {
                const isMissing = missing.has(q.id);
                return (
                <article
                  key={q.id}
                  className={`border-t pt-5 ${isMissing ? "border-red-400" : "border-aviana-border"}`}
                  data-testid={`se-question-${q.id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9px] tracking-[0.18em] uppercase bg-aviana-plum/10 text-aviana-plum rounded-full px-2 py-0.5">Required</span>
                    {isMissing && <span className="text-[9px] tracking-[0.18em] uppercase bg-red-100 text-red-700 rounded-full px-2 py-0.5">Please answer</span>}
                  </div>
                  <div className="aviana-richtext text-aviana-ink text-[15px] mb-3" dangerouslySetInnerHTML={{ __html: q.question_html || q.title || "" }} />
                  {q.choices && q.choices.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {q.choices.map((c, cIdx) => (
                        <label key={cIdx} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${answers[q.id] === cIdx ? "border-aviana-gold bg-aviana-gold/10 text-aviana-ink" : "border-aviana-border text-aviana-muted"}`}>
                          <input
                            type="radio"
                            name={q.id}
                            className="hidden"
                            checked={answers[q.id] === cIdx}
                            onChange={() => {
                              setAnswers({...answers, [q.id]: cIdx});
                              if (isMissing) setMissing((m) => { const n = new Set(m); n.delete(q.id); return n; });
                            }}
                          />
                          {c.text}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={answers[q.id] || ""}
                      onChange={(e) => {
                        setAnswers({...answers, [q.id]: e.target.value});
                        if (isMissing) setMissing((m) => { const n = new Set(m); n.delete(q.id); return n; });
                      }}
                      className={`w-full bg-white border rounded-lg px-4 py-3 text-sm outline-none focus:border-aviana-gold/60 ${isMissing ? "border-red-400" : "border-aviana-border"}`}
                      rows={2}
                      data-testid={`se-answer-${q.id}`}
                    />
                  )}
                </article>
                );
              })}
            </div>

            <div className="mt-8 flex gap-3">
              <button type="submit" disabled={busy} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="submit-self-eval">
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Save self-evaluation"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary !py-2.5 !px-5 !text-[11px]">Cancel</button>
            </div>
            <p className="mt-7 text-center font-serif italic text-aviana-muted text-[14px]">
              You're doing great! Be Bold, Be Confident, Be Fully Present with Each Moment. Love, Light, &amp; Bliss.
            </p>
          </form>
        ) : (
          <div className="space-y-3" data-testid="se-list">
            {mine.length === 0 ? (
              <p className="text-aviana-muted text-center py-12">No self-evaluations yet. Click "Add a self-evaluation" to begin.</p>
            ) : mine.map((m) => (
              <div key={m.id} className="bg-aviana-cream rounded-2xl p-6 border border-aviana-border">
                <p className="overline-plum text-[10px] mb-2">{m.class_date} · {m.type_of_class} · {m.number_of_students} student{m.number_of_students === 1 ? "" : "s"}</p>
                <p className="font-display text-lg text-aviana-ink">{m.style_focus || "Self-evaluation"}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────  STUDENT: TEACHING HOURS LOG  ─────────────────────────

export function TeachingHoursPage() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_hours: 0, total_minutes: 0 });
  const [showForm, setShowForm] = useState(false);
  const emptyForm = () => ({ date: new Date().toISOString().slice(0, 10), duration_minutes: 60, location: "", student_count: 1, notes: "" });
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null); // null when creating; hour id when editing
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/lms/teaching-hours");
    setItems(data.items); setTotals({ total_hours: data.total_hours, total_minutes: data.total_minutes });
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const startEdit = (h) => {
    setForm({
      date: h.date,
      duration_minutes: h.duration_minutes,
      location: h.location || "",
      student_count: h.student_count || 1,
      notes: h.notes || "",
    });
    setEditingId(h.id);
    setShowForm(true);
    // Scroll form into view
    setTimeout(() => document.querySelector('[data-testid="hours-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/lms/teaching-hours/${editingId}`, form);
        toast.success("Session updated");
      } else {
        await api.post("/lms/teaching-hours", form);
        toast.success("Hours logged");
      }
      cancelForm();
      load();
    } catch (_e) { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete the ${h.duration_minutes}-minute session from ${h.date}? This can't be undone.`)) return;
    try {
      await api.delete(`/lms/teaching-hours/${h.id}`);
      toast.success("Session deleted");
      load();
    } catch (_e) { toast.error("Could not delete"); }
  };

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="teaching-hours-page">
      <div className="container-aviana max-w-4xl">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> My dashboard
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="overline-plum mb-3">Module 3</p>
            <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Teaching hours <em className="italic gold-text">({totals.total_hours}/20)</em></h1>
          </div>
          <button onClick={() => { setEditingId(null); setForm(emptyForm()); setShowForm(true); }} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="add-hours-btn">
            <Plus size={14} /> Log a session
          </button>
        </div>

        {/* Progress ring */}
        <div className="bg-aviana-cream rounded-2xl p-6 border border-aviana-border mb-6">
          <div className="h-3 rounded-full bg-aviana-border overflow-hidden">
            <div className="h-full bg-aviana-gold transition-all" style={{ width: `${Math.min(100, (totals.total_hours / 20) * 100)}%` }} />
          </div>
          <p className="mt-3 text-[12px] text-aviana-muted">{totals.total_minutes} minutes total · {Math.max(0, 20 - totals.total_hours)} hours remaining toward your 20-hour requirement</p>
        </div>

        {showForm && (
          <form onSubmit={submit} className="bg-aviana-cream rounded-2xl p-8 border border-aviana-border mb-6" data-testid="hours-form">
            <h2 className="font-display text-2xl text-aviana-ink mb-6">{editingId ? "Edit teaching session" : "New teaching session"}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({...form, date: v})} testid="hours-date" required />
              <Field label="Duration (minutes)" type="number" value={form.duration_minutes} onChange={(v) => setForm({...form, duration_minutes: parseInt(v) || 0})} testid="hours-duration" required />
              <Field label="Location" value={form.location} onChange={(v) => setForm({...form, location: v})} testid="hours-location" />
              <Field label="Number of students" type="number" value={form.student_count} onChange={(v) => setForm({...form, student_count: parseInt(v) || 1})} testid="hours-student-count" />
            </div>
            <label className="block mt-4">
              <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted">Notes (optional)</span>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="mt-2 w-full bg-white border border-aviana-border rounded-lg px-4 py-3 outline-none focus:border-aviana-gold/60" data-testid="hours-notes" />
            </label>
            <div className="mt-6 flex gap-3">
              <button type="submit" disabled={busy} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="submit-hours">
                {busy ? <Loader2 className="animate-spin" size={14} /> : (editingId ? "Save changes" : "Save session")}
              </button>
              <button type="button" onClick={cancelForm} className="btn-secondary !py-2.5 !px-5 !text-[11px]">Cancel</button>
            </div>
          </form>
        )}

        {items.length === 0 ? (
          <p className="text-aviana-muted text-center py-12">No teaching sessions logged yet.</p>
        ) : (
          <div className="space-y-3" data-testid="hours-list">
            {items.map((h) => (
              <div key={h.id} className="bg-aviana-cream rounded-2xl p-6 border border-aviana-border flex items-center gap-5" data-testid={`hours-row-${h.id}`}>
                <div className="font-display text-3xl text-aviana-gold">{h.duration_minutes}<span className="text-base text-aviana-muted">m</span></div>
                <div className="flex-1">
                  <p className="font-display text-lg text-aviana-ink">{h.date}</p>
                  <p className="text-[12px] text-aviana-muted">{h.location || "—"} · {h.student_count} student{h.student_count === 1 ? "" : "s"}</p>
                  {h.notes && <p className="mt-2 text-[13px] text-aviana-muted italic">{h.notes}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => startEdit(h)}
                    className="p-2 rounded-lg text-aviana-muted hover:text-aviana-plum hover:bg-aviana-plum/10 transition-colors"
                    aria-label="Edit session"
                    title="Edit this session"
                    data-testid={`edit-hours-${h.id}`}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => remove(h)}
                    className="p-2 rounded-lg text-aviana-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                    aria-label="Delete session"
                    title="Delete this session"
                    data-testid={`delete-hours-${h.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────  STUDENT EVALUATIONS RECEIVED  ─────────────────────────

export function MyStudentEvaluationsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], count: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/lms/student-evaluations/me").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const shareLink = canonicalUrl(`/evaluate/${user?.id}`);

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="my-evaluations-page">
      <div className="container-aviana max-w-4xl">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> My dashboard
        </Link>
        <p className="overline-plum mb-3">Module 3</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Student evaluations <em className="italic gold-text">({data.count}/10)</em></h1>

        <div className="mt-8 bg-aviana-plumDeep rounded-2xl p-7 text-aviana-cream">
          <p className="overline text-aviana-goldSoft mb-3">Your share link</p>
          <p className="font-serif italic text-[15px] mb-4 text-aviana-cream/85">Share this with your practice teaching students. They'll fill out the evaluation form — it auto-counts toward your requirement.</p>
          <div className="flex items-center gap-3 bg-aviana-plumDeep/60 rounded-lg px-4 py-3 border border-aviana-gold/30">
            <code className="text-[13px] text-aviana-goldSoft flex-1 truncate">{shareLink}</code>
            <button onClick={() => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="text-aviana-goldSoft hover:text-aviana-cream" data-testid="copy-share-link-page">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-3" data-testid="evals-list">
          {data.items.length === 0 ? (
            <p className="text-aviana-muted text-center py-12">No evaluations received yet. Share your link with your students.</p>
          ) : data.items.map((m) => (
            <div key={m.id} className="bg-aviana-cream rounded-2xl p-6 border border-aviana-border">
              <p className="overline-plum text-[10px] mb-2">{new Date(m.submitted_at).toLocaleDateString()} · {m.class_date || "—"} · {m.location || "—"}</p>
              <p className="font-display text-lg text-aviana-ink">From {m.student_name}</p>
              {Object.entries(m.answers || {}).filter(([k, v]) => typeof v === "string" && v && !k.startsWith("_")).slice(0, 3).map(([k, v]) => (
                <p key={k} className="mt-2 text-[13px] text-aviana-muted italic">"{v}"</p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
